import { Component } from '@ydegouvello/ui';

export abstract class AbstractComponent extends Component {
  constructor(
    name: string,
    parent?: { readonly containerHtmlElementSelector: string; readonly component: Component },
    protected readonly refreshIntervalMs: number = 0,
  ) {
    super(name, parent, { path: `../components/${name}.html` });
  }

  get overlayLoaded(): boolean {
    return !!document.getElementById('overlay');
  }

  protected async setLoading(loading: boolean): Promise<void> {
    if (loading) {
      await this.loadOverlay(`<img src="assets/loading.svg" alt="Loading..." />`);
    } else {
      await this.unloadOverlay();
    }
  }

  protected async setError(message = 'Une erreur est survenue.'): Promise<void> {
    await this.loadOverlay(message);
    this.setTimeout(() => void this.unloadOverlay(), 3000);
  }

  protected async loadOverlay(innerHTML?: string): Promise<HTMLDivElement | undefined> {
    if (this.overlayLoaded) return undefined;

    const overlay = document.createElement('div');
    overlay.id = 'overlay';
    if (innerHTML) overlay.innerHTML = innerHTML;
    for (const component of this.children) {
      const parentElement = component.parent?.containerHtmlElementSelector;
      if (typeof parentElement === 'string' && overlay.querySelector(parentElement)) {
        await component.load();
      }
    }
    document.body.classList.add('overlayed');
    document.body.appendChild(overlay);
    return overlay;
  }

  protected async unloadOverlay(): Promise<void> {
    const overlay = document.getElementById('overlay');
    if (!overlay) return;
    for (const component of Component.all) {
      if (component.htmlElement && overlay.contains(component.htmlElement)) {
        await component.unload();
      }
    }
    document.body.classList.remove('overlayed');
    overlay.remove();
  }

  protected async window(innerHTML?: string, className?: string): Promise<HTMLDivElement> {
    const overlay = await this.loadOverlay();
    if (!overlay) throw new Error('Overlay is already loaded');

    const windowElement = document.createElement('div');
    windowElement.className = 'window' + (className ? ` ${className}` : '');
    windowElement.innerHTML = `
      <button class="close" title="Fermer la fenêtre">x</button>
      <div class="window-content">${innerHTML ?? ''}</div>
    `;
    overlay.appendChild(windowElement);

    const closeButton = overlay.querySelector<HTMLButtonElement>('.close');
    if (!closeButton) throw new Error('Close button not found in window');
    this.addEventListener({
      element: closeButton,
      eventName: 'click',
      handler: () => {
        this.removeEventListener({ element: closeButton });
        void this.unloadOverlay();
      },
    });
    return overlay;
  }
}
